/**
 * Window-framework types — the UNIFIED window-registration engine.
 *
 * Phase 1 of the UI-framework mega-arc GENERALIZES the 🛠 debug-tools kit
 * (the cleanest of the 5 legacy UI registration paths) into the ONE engine
 * that will later also serve gameplay windows + diagnostic panels. This file
 * owns the generic shapes; the kit's 24 tools are the first consumer, all
 * `kind: 'tool'`.
 *
 * Drift guards (kept verbatim from the kit):
 *   - `WindowId` is a HAND-WRITTEN union (not derived from WINDOW_REGISTRY) —
 *     the tsc-level guard. Adding a window means touching this union AND the
 *     per-kind content map AND the registry, or the build fails.
 *   - `WindowDef` is a DISCRIMINATED UNION on `kind` so later phases just add
 *     registry entries under the right variant. `gameplay`/`diagnostic` carry
 *     only the base today (no extra fields yet) — they exist so the union is
 *     ready when those phases land.
 */

import type React from 'react';

// ── Window identity (hand-written drift guard — the 24 current tool ids) ──

export type WindowId =
  // ── tool kind (the 🛠 debug-tools kit) ──
  | 'latency'
  | 'netTraffic'
  | 'netobsSparkline'
  | 'netSpike'
  | 'netcodeAlloc'
  | 'tickBadge'
  | 'statusBar'
  | 'debugInfo'
  | 'teleport'
  | 'eventRate'
  | 'lagRootCause'
  | 'positionTracker'
  | 'frameJitter'
  | 'smoothnessDashboard'
  | 'posDrift'
  | 'interpTuning'
  | 'systemHealth'
  | 'levelDiag'
  | 'perfGraph'
  | 'physicsSafety'
  | 'mpDiagnostics'
  | 'physicsDiag'
  | 'renderingDiag'
  | 'authorityOverlay'
  | 'projectileTracker'
  | 'sceneFxTuner'
  // ── diagnostic kind (Phase 3 — folded from the standalone observational
  //    panels that used to live behind DiagnosticsContext flags +
  //    DiagnosticsOverlay; now first-class engine windows). ──
  | 'targetAbilityCheck'
  | 'abilityLifecycle'
  | 'visualLifecycle'
  | 'targetLifecycle'
  | 'abilityTestRig'
  | 'abilityGrounds'
  | 'netobsPacketLog'
  | 'netobsLatencyHeatmap'
  | 'schemaGhost'
  | 'raceRunPerf'
  // ── gameplay kind (Phase 2 — folded from the legacy UIContext-flag +
  //    GameUIOverlay-inline-mount path, the last legacy window path). These
  //    render on EVERY surface (like diagnostic) and are opened from the QUICK
  //    MENU via windowBridge. Batch 1 = player-FREE windows only. ──
  | 'settings'
  | 'keyBindings'
  | 'twitchPiP'
  | 'bank'
  | 'crafting'
  | 'lobbyTools'
  | 'trade'
  | 'party'
  | 'jobs'
  | 'talents'
  | 'equipment'
  | 'inventory'
  | 'spells'
  | 'quests'
  | 'speedrunRecords'
  | 'worldMap'
  | 'leaderboard'
  | 'inspect'
  | 'chestLoot'
  | 'simulationPanel'
  | 'logViewer'
  | 'npcWindowViewer'
  | 'devTools'
  | 'qaPanel'
  | 'contractDashboard'
  // 2026-06-12 HUD fold (Phase 4) — the four DEV-BAR windows HUD used to
  // mount behind local useState toggles, now engine-registered.
  | 'cameraControl'
  | 'simulationTools'
  | 'mapTools'
  | 'mapPicker'
  // ── GM Event windows (kind: 'gameplay') ──
  //    gmEventCreator was RETIRED into gmEventManager (Event Control, E.3
  //    2026-07-10) — one state-aware window owns create + lifecycle.
  | 'gmEventAnnouncement'
  | 'gmEventScoreboard'
  | 'gmEventManager'
  // ── Race lobby window (M7) — draggable framed participant list on the
  //    window substrate. Auto-opens on gm_race_lobby during 'announced'.
  | 'raceLobby'
  // ── GM tool windows (kind: 'gameplay', category: 'gm' — 2026-07-06).
  //    UI over the slash-command surface; server-side COMMAND_PERMISSIONS
  //    gates every action. Roadmap for the section lives in registry.ts. ──
  | 'gmLobbyTools'
  | 'gmPlayerTools'
  | 'gmHazardLab'
  | 'gmSoundDebug';

// ── Kinds + categories ──────────────────────────────────────────────

/** The three window classes the engine is generic over. Only `tool` has
 *  registry entries today; `gameplay`/`diagnostic` are added in later phases. */
export type WindowKind = 'gameplay' | 'diagnostic' | 'tool';

/** Hub categories — used to group windows inside the suite container.
 *  Doesn't affect floating-window behavior.
 *
 *  2026-06-11: the old 'diagnostics' bucket was dissolved — it was never in
 *  the hub sidebar's render order, so its 8 windows (all the ability/
 *  lifecycle checkers) were invisible except via "all on" (DJ report). The
 *  ability checkers now have their own 'abilities' section; the netobs/schema
 *  ones live under 'netcode' where they belong. CATEGORY_META's insertion
 *  order IS the sidebar section order (DebugSuiteContainer derives it), so
 *  every category is rendered by construction — a category can no longer be
 *  defined but unlisted. */
export type WindowCategory = 'netcode' | 'abilities' | 'perf' | 'debug' | 'sound' | 'world' | 'qa' | 'gm' | 'game';

export const CATEGORY_META: Record<WindowCategory, { label: string; icon: string }> = {
  netcode:   { label: 'Netcode', icon: '📡' },
  abilities: { label: 'Abilities', icon: '✨' },
  perf:      { label: 'Frame & Perf', icon: '🎞' },
  debug:     { label: 'Debug', icon: '🐛' },
  // 2026-07-11: dedicated sound section (DJ ask) — the 🔊 Sound Debug card
  // (trace truth + test fires + engine health + the embedded 🎛️ bench) gets
  // its own home in the Shift+D kit instead of hiding among 10 debug rows.
  sound:     { label: 'Sound', icon: '🔊' },
  // 2026-07-11 catalog tightening (DJ ask): the old 'tools' junk drawer
  // (12 windows) dissolved into task-shaped homes — 'world' (maps, teleport,
  // camera, scene FX, level truth) + 'qa' (QA checklist, contracts, sim
  // panels, NPC window preview); Debug Console + Dev Tools → 'debug'.
  world:     { label: 'World & Maps', icon: '🗺' },
  qa:        { label: 'QA & Sim', icon: '🧪' },
  // 2026-07-06: GM control surfaces get their own section (DJ ask) — event
  // manager/creator plus the new lobby/player tool panels. Everything here is
  // UI over the slash-command registry, so COMMAND_PERMISSIONS gates it
  // server-side regardless of who opens the window.
  gm:        { label: 'GM', icon: '🛡️' },
  // 2026-06-12: player-facing gameplay windows get their own section — they
  // were all dumped into 'tools' during the Phase-2 migration, ballooning the
  // hub's Tools sidebar to 21 rows (Bank/Trade/Twitch inside a debug hub).
  game:      { label: 'Game Windows', icon: '🎮' },
};

// ── Window definitions (discriminated union on `kind`) ───────────────

/** Fields shared by every window kind (was DebugToolDef + framework extras). */
export interface WindowDefBase {
  id: WindowId;
  label: string;
  icon: string;
  description: string;
  category: WindowCategory;
  /** Initial top-left of the ToolWindow chrome. Optional — when omitted the
   *  host lets ToolWindow apply its own default (horizontally centered, y=50),
   *  which is exactly how the legacy Settings window (no explicit position)
   *  opened, so the fold is a zero-visual-change move. Every tool/diagnostic
   *  entry sets it explicitly; gameplay action windows may rely on centering. */
  defaultPosition?: { x: number; y: number };
  defaultSize: { width: number; height: number };
  /** Per-window resize clamps for the ToolWindow chrome. Optional — the host
   *  falls back to its generic clamps when omitted. Carried over 1:1 from the
   *  folded panels so resizing feels identical to the standalone version. */
  minSize?: { width: number; height: number };
  maxSize?: { width: number; height: number };
  /** Persisted z-index for the ToolWindow (folded panels used 500). */
  zIndex?: number;
  /** Restrict to specific surfaces. Omitted = no surface restriction beyond
   *  the engine's per-kind gating (tool windows are debug-surface-gated). */
  surfaces?: readonly string[];
  /** Persist this window's open-state. Defaults to its kind's default
   *  (tool windows persist under `debug_tools_v2`). */
  persist?: boolean;
  /** Persist ToolWindow position/size/minimized geometry. Defaults true.
   *  Deterministic lab surfaces can opt out so every visit starts from the
   *  registry's collision-free layout while remaining draggable in-session. */
  persistGeometry?: boolean;
  /** How the open window renders. Default 'window' = draggable ToolWindow
   *  chrome. 'tome' = draggable Tome window presentation with parchment and title block.
   *  'overlay' = bare full-screen Content with NO chrome — for
   *  modal-style surfaces like the Map Picker (WC3-style select screen)
   *  where a floating window frame would be wrong. Overlay content owns its
   *  own close affordance via useWindow(id).closeWindow. */
  presentation?: 'window' | 'overlay' | 'tome';
  /** Query-param names that seed this window OPEN on first load (no persisted
   *  state). Preserves the kit's exact seedFromUrl param table per-window.
   *  Diagnostic windows reuse this for their old DiagnosticsContext URL seeds
   *  (?devPanelsOn=1 / ?netobs=1 / ?diag=1). */
  urlSeed?: readonly string[];
}

/** Tool windows — the 🛠 kit. Needs nothing beyond the base today. */
export interface ToolWindowDef extends WindowDefBase {
  kind: 'tool';
}

/** Gameplay windows — added in a later phase. Base-only for now. */
export interface GameplayWindowDef extends WindowDefBase {
  kind: 'gameplay';
}

/** Diagnostic panels (Phase 3) — observational windows folded from the old
 *  DiagnosticsContext flags. Unlike tool windows they render on EVERY surface
 *  (gameplay + debug), since DJ opens them to watch live combat/netcode on
 *  /brawl etc. Some carry a pure side-effect toggle instead of (or alongside)
 *  visible UI — `schemaGhost` flips a renderer window-global with no panel. */
export interface DiagnosticWindowDef extends WindowDefBase {
  kind: 'diagnostic';
}

export type WindowDef = ToolWindowDef | GameplayWindowDef | DiagnosticWindowDef;

/** A per-kind id→component map. Typed over the full WindowId so the merged
 *  map (content.ts) is exhaustive across every registered window. */
export type WindowContentMap = Partial<Record<WindowId, React.FC>>;
